<!-- NOTE: slot text should be wrapped in a <span> to avoid default styles (e.g. <p>) -->
<script setup lang="ts">
import { onClickOutside, onKeyStroke } from '@vueuse/core';
import {
  computed,
  onMounted,
  onUnmounted,
  useSlots,
  useTemplateRef,
} from 'vue';

import { isSmallModal } from '../modals.helper';
import AppOverlay from '../AppOverlay.vue';
import AppButton from '../../appButton/AppButton.vue';
import AppIconButton from '../../appIconButton/AppIconButton.vue';
import { KeyboardKeyCode } from '../../../models';
import {
  blockBackgroundScroll,
  enableBackgroundScroll,
} from '../../../helpers/blockBackgroundScroll';
import AppFadeTransition from '../../transitions/appFadeTransition/AppFadeTransition.vue';

interface AppConfirmationModalProps {
  closeButton:
    | { type: 'no-button' }
    | { type: 'with-button'; ariaLabel: string };
}

const props = defineProps<AppConfirmationModalProps>();

export interface AppConfirmationModalEmits {
  (eventName: 'close'): void;
  (eventName: 'cancel'): void;
  (eventName: 'confirm'): void;
}

const emit = defineEmits<AppConfirmationModalEmits>();

const confirmationModalRef = useTemplateRef('confirmationModalRef');

const slots = useSlots();
const hasCancelButton = computed(() => !!slots['cancelButton']);
const hasApplyButton = computed(() => !!slots['applyButton']);
const hasTitle = computed(() => !!slots['title']);
const hasHeaderTitle = computed(() => !!slots['headerTitle']);

const shouldAddSidePadding = computed(
  () => !isSmallModal.value && hasCancelButton.value && hasApplyButton.value
);
const shouldAddBottomPadding = computed(
  () =>
    (hasCancelButton.value || hasApplyButton.value) &&
    !(hasCancelButton.value && hasApplyButton.value)
);

onClickOutside(
  confirmationModalRef,
  () => props.closeButton.type === 'with-button' && emit('close')
);
onKeyStroke(
  KeyboardKeyCode.Escape,
  () => props.closeButton.type === 'with-button' && emit('close')
);

onMounted(() => blockBackgroundScroll());

onUnmounted(() => enableBackgroundScroll());
</script>

<template>
  <AppOverlay :is-modal="true" transition-time="short">
    <AppFadeTransition transition-time="short" transition-type="fade-in">
      <aside
        ref="confirmationModalRef"
        class="app-confirmation-modal"
        tabindex="0"
      >
        <header class="modal-header">
          <div v-if="hasHeaderTitle" class="header-title">
            <slot name="headerTitle" />
          </div>
          <AppIconButton
            v-if="props.closeButton.type === 'with-button'"
            type="secondary"
            state="enabled"
            size="medium"
            :ariaLabel="props.closeButton.ariaLabel"
            :icon="{ name: 'close' }"
            @click="emit('close')"
          />
        </header>

        <div class="modal-body">
          <span v-if="hasTitle" class="title">
            <slot name="title" />
          </span>
          <span
            class="content"
            :class="{
              'side-padding': shouldAddSidePadding,
            }"
          >
            <slot name="content" />
          </span>
        </div>

        <footer
          v-if="hasCancelButton || hasApplyButton"
          class="modal-footer"
          :class="{
            'padding-bottom': shouldAddBottomPadding,
          }"
        >
          <AppButton
            v-if="hasCancelButton"
            class="button"
            type="secondary"
            state="enabled"
            size="large"
            :wide="isSmallModal || hasApplyButton"
            @click="emit('cancel')"
          >
            <slot name="cancelButton" />
          </AppButton>

          <AppButton
            v-if="hasApplyButton"
            class="button"
            type="primary"
            state="enabled"
            size="large"
            :wide="isSmallModal || hasCancelButton"
            @click="emit('confirm')"
          >
            <slot name="applyButton" />
          </AppButton>
        </footer>
      </aside>
    </AppFadeTransition>
  </AppOverlay>
</template>

<style lang="scss" scoped>
@use '../../../styles/media' as *;
@use '../../../styles/fonts' as *;

@mixin title {
  width: 100%;
  color: var(--color-neutral-70);
  @include font-label-xl-bold;
}

.app-confirmation-modal {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: stretch;
  gap: var(--spacing-32);

  padding: var(--spacing-24);

  z-index: var(--modal-z-index);
  background-color: var(--color-white);
  border-radius: var(--border-radius-12);

  width: calc(100% - 2 * var(--spacing-16));
  max-width: 700px;
  max-height: calc(100dvh - 2 * var(--spacing-16));
  max-height: calc(100vh - 2 * var(--spacing-16));
  overflow: auto;

  .padding-bottom {
    padding-bottom: var(--spacing-24);
  }

  .modal-header {
    display: flex;
    justify-content: flex-end;
    align-items: center;

    .header-title {
      @include title;
      flex: 1;
    }
  }

  .modal-body {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    margin-bottom: var(--spacing-32);
    gap: var(--spacing-16);

    .side-padding {
      padding: 0 var(--spacing-24);
    }

    .title {
      @include title;
    }
    .content {
      color: var(--color-neutral-70);
      @include font-body-l-light;
    }
  }

  .modal-footer {
    display: flex;
    flex-direction: row;
    justify-content: center;
    gap: var(--spacing-16);
    flex-wrap: nowrap;

    @include for-less-or-equal-than-900-px {
      flex-direction: column;
    }

    .button {
      min-width: 300px;
    }
  }
}
</style>
